import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import { useUserStore } from "../../../store/userStore";
import { useSupportChatStore } from "../../../store/supportChatStore";
import type { ChatParticipant, SidebarTab } from "../../../store/supportChatStore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faPlus,
  faUsers,
  faUser,
  faCircle,
  faComments,
  faRoute,
  faTruck,
} from "@fortawesome/free-solid-svg-icons";
import NewConversationModal from "./NewConversationModal";

interface ChatSidebarProps {
  onSelectConversation: (conversationId: string) => void;
}

export default function ChatSidebar({ onSelectConversation }: ChatSidebarProps) {
  const { t } = useTranslation("support");
  const { isDarkMode } = useTheme();
  const user = useUserStore((state) => state.user);
  const conversations = useSupportChatStore((s) => s.conversations);
  const activeConversationId = useSupportChatStore((s) => s.activeConversationId);
  const onlineUsers = useSupportChatStore((s) => s.onlineUsers);
  const isLoadingConversations = useSupportChatStore((s) => s.isLoadingConversations);
  const activeTab = useSupportChatStore((s) => s.activeTab);
  const setActiveTab = useSupportChatStore((s) => s.setActiveTab);

  const [searchQuery, setSearchQuery] = useState("");
  const [isNewConvModalOpen, setIsNewConvModalOpen] = useState(false);

  const getConversationName = useCallback(
    (
      participants: ChatParticipant[],
      name: string | null | undefined,
      currentUserId: string | undefined,
    ): string => {
      if (name) return name;
      const otherParts = participants.filter((p) => p.uid !== currentUserId);
      if (otherParts.length === 0) return t("chat.you");
      return otherParts.map((p) => p.display_name).join(", ");
    },
    [t],
  );

  // Memoize derived collections so typing in the search box doesn't trigger
  // a full re-scan of `conversations` on every keystroke (the list re-renders
  // anyway because of the input state, but the work is now O(1) per render).
  const filteredConversations = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return conversations
      .filter((conv) =>
        activeTab === "viajes" ? !!conv.trip_id : !conv.trip_id,
      )
      .filter((conv) => {
        if (!query) return true;
        const name = getConversationName(conv.participants, conv.name, user?.id);
        const tripLabel = conv.trip_id
          ? `${conv.trip_id} ${conv.trip_name ?? ""}`
          : "";
        return (
          name.toLowerCase().includes(query) ||
          tripLabel.toLowerCase().includes(query)
        );
      });
  }, [conversations, activeTab, searchQuery, user?.id, getConversationName]);

  const { chatUnreadCount, tripUnreadCount } = useMemo(() => {
    let chats = 0;
    let trips = 0;
    for (const c of conversations) {
      if (c.trip_id) trips += c.unread_count;
      else chats += c.unread_count;
    }
    return { chatUnreadCount: chats, tripUnreadCount: trips };
  }, [conversations]);

  function formatTime(timestamp: number | undefined): string {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } else if (diffDays === 1) {
      return t("chat.yesterday");
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: "short" });
    }
    return date.toLocaleDateString([], { month: "short", day: "numeric" });
  }

  const tabs: { id: SidebarTab; label: string; icon: typeof faComments }[] = [
    { id: "chats", label: t("tabs.chats"), icon: faComments },
    { id: "viajes", label: t("tabs.viajes"), icon: faRoute },
  ];

  return (
    <div className="h-full min-h-0 flex flex-col">
      {/* Header */}
      <div
        className={`px-4 py-3 border-b flex items-center justify-between ${
          isDarkMode ? "border-dark-border" : "border-gray-200"
        }`}
      >
        <h2
          className={`text-lg font-semibold ${
            isDarkMode ? "text-white" : "text-gray-900"
          }`}
        >
          {t("chat.title")}
        </h2>
        <button
          onClick={() => setIsNewConvModalOpen(true)}
          className={`p-2 rounded-lg transition-colors ${
            isDarkMode
              ? "hover:bg-dark-hover text-gray-300"
              : "hover:bg-gray-100 text-gray-600"
          }`}
          title={t("chat.newConversation")}
        >
          <FontAwesomeIcon icon={faPlus} />
        </button>
      </div>

      {/* Tabs */}
      <div
        className={`flex border-b ${
          isDarkMode ? "border-dark-border" : "border-gray-200"
        }`}
      >
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          const badgeCount = tab.id === "chats" ? chatUnreadCount : tripUnreadCount;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-xs font-medium transition-colors relative ${
                isActive
                  ? isDarkMode
                    ? "text-primary-400 border-b-2 border-primary-400"
                    : "text-primary-600 border-b-2 border-primary-600"
                  : isDarkMode
                    ? "text-gray-400 hover:text-gray-200"
                    : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <FontAwesomeIcon icon={tab.icon} className="text-sm" />
              {tab.label}
              {badgeCount > 0 && (
                <span className="px-1.5 py-0.5 text-[9px] font-bold text-white bg-primary-500 rounded-full min-w-[16px] text-center leading-none">
                  {badgeCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="px-4 py-2">
        <div
          className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
            isDarkMode ? "bg-dark-hover" : "bg-gray-100"
          }`}
        >
          <FontAwesomeIcon
            icon={faSearch}
            className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          />
          <input
            type="text"
            placeholder={activeTab === "viajes" ? t("tabs.searchViajes") : t("chat.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`flex-1 bg-transparent text-sm outline-none ${
              isDarkMode ? "text-white placeholder:text-gray-500" : "text-gray-900 placeholder:text-gray-400"
            }`}
          />
        </div>
      </div>

      {/* Conversations list */}
      <div className="flex-1 overflow-y-auto">
        {isLoadingConversations ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary-500 border-t-transparent" />
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <FontAwesomeIcon
              icon={activeTab === "viajes" ? faTruck : faUsers}
              className={`text-3xl mb-3 ${isDarkMode ? "text-gray-600" : "text-gray-300"}`}
            />
            <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
              {activeTab === "viajes" ? t("tabs.noViajes") : t("chat.noConversations")}
            </p>
          </div>
        ) : (
          filteredConversations.map((conv) => {
            const isActive = conv.id === activeConversationId;
            const others = conv.participants.filter((p) => p.uid !== user?.id);
            const convName = getConversationName(conv.participants, conv.name, user?.id);
            const isOtherOnline = others.some((p) => onlineUsers[p.uid]);

            return (
              <button
                key={conv.id}
                onClick={() => onSelectConversation(conv.id)}
                className={`w-full px-4 py-3 flex items-center gap-3 transition-colors text-left ${
                  isActive
                    ? isDarkMode
                      ? "bg-dark-hover border-l-2 border-primary-500"
                      : "bg-primary-50 border-l-2 border-primary-500"
                    : isDarkMode
                      ? "hover:bg-dark-hover"
                      : "hover:bg-gray-50"
                }`}
              >
                {/* Avatar */}
                <div className="relative flex-shrink-0">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      conv.trip_id
                        ? "bg-amber-500"
                        : conv.type === "group"
                          ? "bg-violet-500"
                          : "bg-primary-500"
                    }`}
                  >
                    <FontAwesomeIcon
                      icon={conv.trip_id ? faTruck : conv.type === "group" ? faUsers : faUser}
                      className="text-white text-sm"
                    />
                  </div>
                  {isOtherOnline && !conv.trip_id && (
                    <FontAwesomeIcon
                      icon={faCircle}
                      className="absolute bottom-0 right-0 text-green-500 text-[8px] bg-white rounded-full"
                    />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span
                        className={`text-sm font-medium truncate ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {convName}
                      </span>
                    </div>
                    <span
                      className={`text-xs flex-shrink-0 ml-2 ${
                        isDarkMode ? "text-gray-500" : "text-gray-400"
                      }`}
                    >
                      {formatTime(conv.last_message_at ?? undefined)}
                    </span>
                  </div>
                  {conv.trip_id && (
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {conv.route_name && (
                        <span className={`text-[10px] font-medium flex items-center gap-1 ${
                          isDarkMode ? "text-blue-400" : "text-blue-600"
                        }`}>
                          <FontAwesomeIcon icon={faRoute} className="text-[8px]" />
                          {conv.route_name}
                        </span>
                      )}
                      {!conv.route_name && (
                        <span className={`text-[10px] font-medium ${
                          isDarkMode ? "text-amber-400" : "text-amber-600"
                        }`}>
                          {conv.trip_name || conv.trip_id}
                        </span>
                      )}
                    </div>
                  )}
                  {conv.trip_id && conv.trip_description && (
                    <p className={`text-[10px] truncate mt-0.5 ${
                      isDarkMode ? "text-gray-500" : "text-gray-400"
                    }`}>
                      {conv.trip_description}
                    </p>
                  )}
                  <div className="flex items-center justify-between mt-0.5">
                    <p
                      className={`text-xs truncate ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      {conv.last_message || t("chat.noMessages")}
                    </p>
                    {conv.unread_count > 0 && (
                      <span className="flex-shrink-0 ml-2 px-1.5 py-0.5 text-[10px] font-bold text-white bg-primary-500 rounded-full min-w-[18px] text-center">
                        {conv.unread_count}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>

      {/* New conversation modal */}
      {isNewConvModalOpen && (
        <NewConversationModal
          onClose={() => setIsNewConvModalOpen(false)}
          onCreated={(conversationId: string) => {
            setIsNewConvModalOpen(false);
            onSelectConversation(conversationId);
          }}
        />
      )}
    </div>
  );
}
